import { useTranslation } from 'react-i18next';
import { useEffect, useId, useState } from 'react';
import { Button } from '../../components/ui/Button';
import confirmStyles from '../../components/ui/ConfirmDialog.module.css';
import { DatePicker } from '../../components/ui/DatePicker';
import { DialogShell } from '../../components/ui/DialogShell';
import { StatusIcon } from '../../components/ui/StatusIcon';
import { useDialogDismiss } from '../../components/ui/dialogDismiss';
import type {
  EditStopRequest,
  OpenStopRequest,
  StopPayer,
  StopReason,
  StopResponse,
} from '../../api/types';
import { istDateTimeInput, istInputToIso } from '../payments/paymentDates';
import {
  STOP_NOTE_MAX,
  STOP_PAYERS,
  STOP_PLACE_MAX,
  STOP_REASONS,
  stopPayerLabel,
  stopProblem,
  stopReasonLabel,
} from './stopLabels';
import { DialogTitleRow } from '../../help/DialogTitleRow';
import sheet from './RecordDelaySheet.module.css';
import styles from './TruckStoppedDialog.module.css';

interface Props {
  isOpen: boolean;
  shipmentId: number;
  /** null = "Truck stopped" (open a stop); an open stop = "Edit stop", prefilled. */
  stop: StopResponse | null;
  onConfirm: (body: OpenStopRequest | EditStopRequest) => void;
  onCancel: () => void;
  loading?: boolean;
  /** Inline refusal from the server; the picks stay as they were. */
  errorMessage?: string | null;
}

function toIso(value: string): string | null {
  try {
    return value ? istInputToIso(value) : null;
  } catch {
    return null;
  }
}

const textOrNull = (value: string) => value.trim() || null;
const sameInstant = (a: string | null, b: string | null) =>
  a === b || (a !== null && b !== null && Date.parse(a) === Date.parse(b));

/**
 * Truck stopped (dnm-work#165 C2): why it stopped, who is responsible, and optionally when it should
 * move again, where, and a note (required for Other). The same sheet edits an open stop and then sends
 * only the fields that changed (PATCH). The stage never changes here (R53).
 */
export function TruckStoppedDialog({
  isOpen,
  shipmentId,
  stop,
  onConfirm,
  onCancel,
  loading,
  errorMessage,
}: Props) {
  const { t } = useTranslation();
  const editing = stop !== null;
  const [reason, setReason] = useState<StopReason | null>(null);
  const [payer, setPayer] = useState<StopPayer | null>(null);
  const [time, setTime] = useState('');
  const [place, setPlace] = useState('');
  const [note, setNote] = useState('');
  const titleId = useId();
  const timeId = useId();
  const placeId = useId();
  const noteId = useId();

  useEffect(() => {
    if (!isOpen) return;
    setReason(stop?.reason ?? null);
    setPayer(stop?.payer ?? null);
    setTime(stop?.expectedResumeAt ? istDateTimeInput(new Date(stop.expectedResumeAt)) : '');
    setPlace(stop?.place ?? '');
    setNote(stop?.note ?? '');
  }, [isOpen, stop]);

  const expectedResumeAt = toIso(time);
  const timeUnreadable = time !== '' && expectedResumeAt === null;
  // The API keeps the expected time when the field is null, so an edit cannot remove it:
  // an emptied time is not a change, and the sheet says so.
  const timeKept = editing && time === '' && stop.expectedResumeAt !== null;
  // An unchanged expected time on an edit is not re-checked against "now": it is not sent.
  const timeChanged = !timeKept && !sameInstant(expectedResumeAt, stop?.expectedResumeAt ?? null);
  const problem = stopProblem({
    reason,
    note,
    place,
    expectedResumeAt: editing && !timeChanged ? null : expectedResumeAt,
  });

  const changes: EditStopRequest = {};
  if (editing) {
    if (reason && reason !== stop.reason) changes.reason = reason;
    if (payer && payer !== stop.payer) changes.payer = payer;
    if (timeChanged) changes.expectedResumeAt = expectedResumeAt;
    // PATCH: null keeps a field, a blank place or note clears it — so a cleared field is sent as ''.
    if (textOrNull(place) !== (stop.place ?? null)) changes.place = place.trim();
    if (textOrNull(note) !== (stop.note ?? null)) changes.note = note.trim();
  }
  const nothingChanged = editing && Object.keys(changes).length === 0;
  const valid =
    reason !== null &&
    (editing || payer !== null) &&
    problem === null &&
    !timeUnreadable &&
    !nothingChanged;

  const dismiss = useDialogDismiss({
    kind: 'form',
    dirty: editing
      ? !nothingChanged
      : reason !== null || payer !== null || place.trim() !== '' || note.trim() !== '',
    busy: loading,
    onClose: onCancel,
  });

  if (!isOpen) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || !reason || loading) return;
    if (editing) {
      onConfirm(changes);
      return;
    }
    onConfirm({
      reason,
      payer: payer!,
      expectedResumeAt,
      place: textOrNull(place),
      note: textOrNull(note),
    });
  };

  const title = editing
    ? t('stops.sheet.titleEdit', { shipmentId })
    : t('stops.sheet.titleOpen', { shipmentId });

  return (
    <DialogShell
      dismiss={dismiss}
      labelledBy={titleId}
      scrimClassName={confirmStyles.backdrop}
      panelClassName={`${confirmStyles.modal} ${sheet.panel}`}
    >
      <div className={confirmStyles.header}>
        <DialogTitleRow context="truck-stopped" title={title}>
          <h3 id={titleId} className={confirmStyles.title}>
            {title}
          </h3>
        </DialogTitleRow>
        <button
          type="button"
          className={confirmStyles.closeBtn}
          onClick={dismiss.requestClose}
          disabled={loading}
          aria-label={t('common.actions.close')}
        >
          &times;
        </button>
      </div>
      <form onSubmit={submit} className={`${confirmStyles.body} ${sheet.body}`}>
        <fieldset className={sheet.choices} disabled={loading}>
          <legend className={sheet.legend}>{t('stops.sheet.reasonLegend')}</legend>
          {STOP_REASONS.map((r, i) => (
            <label
              key={r.code}
              className={`${sheet.choice} ${reason === r.code ? sheet.choiceOn : ''}`}
            >
              <input
                type="radio"
                name="stop-reason"
                value={r.code}
                checked={reason === r.code}
                onChange={() => setReason(r.code)}
                className={sheet.radio}
                data-autofocus={i === 0 ? true : undefined}
              />
              <span className={sheet.icon}>
                <StatusIcon name={r.icon} size={20} />
              </span>
              <span className={sheet.label}>{stopReasonLabel(t, r.code)}</span>
            </label>
          ))}
        </fieldset>

        <fieldset className={`${sheet.choices} ${styles.payers}`} disabled={loading}>
          <legend className={sheet.legend}>{t('stops.sheet.payerLegend')}</legend>
          {STOP_PAYERS.map((p) => (
            <label key={p} className={`${sheet.choice} ${payer === p ? sheet.choiceOn : ''}`}>
              <input
                type="radio"
                name="stop-payer"
                value={p}
                checked={payer === p}
                onChange={() => setPayer(p)}
                className={sheet.radio}
              />
              <span className={sheet.label}>{stopPayerLabel(t, p)}</span>
            </label>
          ))}
        </fieldset>
        {editing && stop.payer === null && payer === null && (
          <span className={styles.notSet}>{stopPayerLabel(t, null)}</span>
        )}
        <span className={styles.hint}>{t('stops.sheet.payerHint')}</span>

        <div className={confirmStyles.reasonWrapper}>
          <label htmlFor={timeId} className={sheet.fieldLabel}>
            {t('stops.sheet.expectedLabel')}
          </label>
          <DatePicker id={timeId} value={time} onChange={setTime} withTime disabled={loading} />
          {timeKept && <span className={styles.hint}>{t('stops.sheet.timeKept')}</span>}
        </div>

        <div className={confirmStyles.reasonWrapper}>
          <label htmlFor={placeId} className={sheet.fieldLabel}>
            {t('stops.sheet.placeLabel')}
          </label>
          <input
            id={placeId}
            className={sheet.textInput}
            value={place}
            onChange={(e) => setPlace(e.target.value)}
            maxLength={STOP_PLACE_MAX}
            disabled={loading}
            placeholder={t('stops.sheet.placePlaceholder')}
          />
        </div>

        <div className={confirmStyles.reasonWrapper}>
          <label htmlFor={noteId} className={sheet.fieldLabel}>
            {reason === 'OTHER' ? t('stops.sheet.noteLabelRequired') : t('stops.sheet.noteLabel')}
          </label>
          <textarea
            id={noteId}
            className={`${sheet.textInput} ${styles.textArea}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={STOP_NOTE_MAX}
            disabled={loading}
            placeholder={t('stops.sheet.notePlaceholder')}
          />
          <span className={sheet.counter}>
            {note.trim().length}/{STOP_NOTE_MAX}
          </span>
        </div>

        {problem && (
          <span className={sheet.warn} data-problem={problem}>
            {t(`stops.problems.${problem}`)}
          </span>
        )}
        {nothingChanged && <span className={styles.hint}>{t('stops.sheet.noChanges')}</span>}

        {errorMessage && (
          <span role="alert" className={confirmStyles.errorText}>
            {errorMessage}
          </span>
        )}

        <div className={`${confirmStyles.actions} ${sheet.actions}`}>
          <Button variant="secondary" onClick={dismiss.requestClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={loading || !valid} busy={loading}>
            {loading
              ? t('stops.sheet.saving')
              : editing
                ? t('stops.sheet.saveEdit')
                : t('stops.sheet.saveOpen')}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
